import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, ChevronDown, ChevronUp, HandCoins, Banknote } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const formatR = (v) => {
  const n = parseFloat(v) || 0;
  return `R ${n.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const today = () => new Date().toISOString().split("T")[0];

export const LoansTab = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedLoan, setExpandedLoan] = useState(null);

  // New loan form
  const [showNewLoan, setShowNewLoan] = useState(false);
  const [newLender, setNewLender] = useState("");
  const [newAmount, setNewAmount] = useState("");
  const [newDate, setNewDate] = useState(today());
  const [newNote, setNewNote] = useState("");

  // Repayment form (per loan)
  const [repayLoanId, setRepayLoanId] = useState(null);
  const [repayAmount, setRepayAmount] = useState("");
  const [repayDate, setRepayDate] = useState(today());
  const [repayNote, setRepayNote] = useState("");

  const fetchLoans = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/financial-summary/loans`);
      setLoans(data);
    } catch {
      toast.error("Failed to load loans");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLoans(); }, [fetchLoans]);

  const handleAddLoan = async () => {
    if (!newLender.trim() || !newAmount || parseFloat(newAmount) <= 0) {
      toast.error("Enter lender name and a positive amount");
      return;
    }
    try {
      await axios.post(`${API}/api/financial-summary/loans`, {
        lender_name: newLender.trim(),
        amount: parseFloat(newAmount),
        date: newDate,
        note: newNote.trim(),
      });
      toast.success(`Loan from ${newLender.trim()} added`);
      setShowNewLoan(false);
      setNewLender(""); setNewAmount(""); setNewDate(today()); setNewNote("");
      fetchLoans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add loan");
    }
  };

  const handleAddRepayment = async (loanId) => {
    if (!repayAmount || parseFloat(repayAmount) <= 0) {
      toast.error("Enter a positive repayment amount");
      return;
    }
    try {
      await axios.post(`${API}/api/financial-summary/loans/${loanId}/repayments`, {
        amount: parseFloat(repayAmount),
        date: repayDate,
        note: repayNote.trim(),
      });
      toast.success("Repayment recorded");
      setRepayLoanId(null);
      setRepayAmount(""); setRepayDate(today()); setRepayNote("");
      fetchLoans();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add repayment");
    }
  };

  const handleDeleteLoan = async (loan) => {
    if (!window.confirm(`Delete loan from ${loan.lender_name} (${formatR(loan.amount)})? This will also delete all repayments.`)) return;
    try {
      await axios.delete(`${API}/api/financial-summary/loans/${loan.id}`);
      toast.success("Loan deleted");
      fetchLoans();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDeleteRepayment = async (loanId, repaymentId) => {
    if (!window.confirm("Delete this repayment?")) return;
    try {
      await axios.delete(`${API}/api/financial-summary/loans/${loanId}/repayments/${repaymentId}`);
      toast.success("Repayment deleted");
      fetchLoans();
    } catch { toast.error("Failed to delete"); }
  };

  const totalLoaned = loans.reduce((s, l) => s + (l.amount || 0), 0);
  const totalRepaid = loans.reduce((s, l) => s + (l.total_repaid || 0), 0);
  const totalOutstanding = loans.reduce((s, l) => s + (l.outstanding || 0), 0);

  if (loading && loans.length === 0) return <div className="text-center py-12 text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="loans-tab">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <div className="text-xs text-blue-700">Total Loaned</div>
          <div className="text-xl font-bold font-mono text-blue-800">{formatR(totalLoaned)}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
          <div className="text-xs text-green-700">Total Repaid</div>
          <div className="text-xl font-bold font-mono text-green-800">{formatR(totalRepaid)}</div>
        </div>
        <div className={`${totalOutstanding > 0 ? "bg-red-50 border-red-200" : "bg-gray-50 border-gray-200"} border rounded-lg p-4 text-center`}>
          <div className={`text-xs ${totalOutstanding > 0 ? "text-red-700" : "text-gray-700"}`}>Outstanding</div>
          <div className={`text-xl font-bold font-mono ${totalOutstanding > 0 ? "text-red-800" : "text-gray-800"}`}>{formatR(totalOutstanding)}</div>
        </div>
      </div>

      {/* Add Loan Button */}
      {!showNewLoan ? (
        <Button onClick={() => setShowNewLoan(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-loan-btn">
          <Plus className="h-4 w-4 mr-1" /> Add New Loan
        </Button>
      ) : (
        <Card className="border-2 border-blue-300">
          <CardContent className="p-4 space-y-3">
            <div className="font-semibold text-blue-800 text-sm">New Loan</div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs">Lender Name</Label>
                <Input
                  value={newLender}
                  onChange={(e) => setNewLender(e.target.value)}
                  placeholder="Who loaned you money?"
                  className="h-9"
                  data-testid="new-loan-lender"
                  autoFocus
                />
              </div>
              <div>
                <Label className="text-xs">Amount</Label>
                <Input
                  type="number" step="0.01"
                  value={newAmount}
                  onChange={(e) => setNewAmount(e.target.value)}
                  placeholder="0.00"
                  className="h-9 font-mono"
                  data-testid="new-loan-amount"
                />
              </div>
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="h-9" />
              </div>
              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Purpose" className="h-9" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddLoan} className="bg-blue-600 hover:bg-blue-700" data-testid="save-loan-btn">Save Loan</Button>
              <Button variant="outline" onClick={() => setShowNewLoan(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loans List */}
      {loans.length === 0 ? (
        <div className="text-center py-12 text-gray-400 border-2 border-dashed rounded-lg">
          <HandCoins className="h-12 w-12 mx-auto mb-2 opacity-50" />
          <p>No loans recorded yet</p>
        </div>
      ) : (
        <div className="space-y-3">
          {loans.map(loan => {
            const isExpanded = expandedLoan === loan.id;
            const pct = loan.amount > 0 ? Math.min(100, (loan.total_repaid / loan.amount) * 100) : 0;
            const isPaidOff = loan.outstanding <= 0;

            return (
              <Card key={loan.id} className={`overflow-hidden ${isPaidOff ? "border-green-300 bg-green-50/30" : ""}`}>
                <div
                  className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedLoan(isExpanded ? null : loan.id)}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isPaidOff ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>
                      <Banknote className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-bold truncate">{loan.lender_name}</div>
                      <div className="text-xs text-gray-500">
                        {loan.date} {loan.note && `- ${loan.note}`}
                        {isPaidOff && <span className="ml-2 text-green-600 font-semibold">PAID OFF</span>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 shrink-0">
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Loaned</div>
                      <div className="font-mono font-semibold">{formatR(loan.amount)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Repaid</div>
                      <div className="font-mono font-semibold text-green-700">{formatR(loan.total_repaid)}</div>
                    </div>
                    <div className="text-right w-28">
                      <div className="text-xs text-gray-500">Outstanding</div>
                      <div className={`font-mono font-bold ${isPaidOff ? "text-green-700" : "text-red-700"}`}>{formatR(loan.outstanding)}</div>
                    </div>
                    <div className="w-24">
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full transition-all ${isPaidOff ? "bg-green-500" : "bg-blue-500"}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-xs text-center text-gray-500 mt-0.5">{pct.toFixed(0)}% repaid</div>
                    </div>
                    {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t bg-gray-50 p-4 space-y-3">
                    {/* Repayment History */}
                    {loan.repayments?.length > 0 ? (
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Repayment History</div>
                        <div className="border rounded bg-white divide-y">
                          {loan.repayments.map(r => (
                            <div key={r.id} className="flex items-center justify-between px-3 py-2 text-sm group">
                              <span className="text-gray-500 w-24">{r.date}</span>
                              <span className="flex-1 truncate text-gray-700">{r.note || "-"}</span>
                              <span className="font-mono font-semibold text-green-700 w-28 text-right">{formatR(r.amount)}</span>
                              <button
                                onClick={(e) => { e.stopPropagation(); handleDeleteRepayment(loan.id, r.id); }}
                                className="opacity-0 group-hover:opacity-100 ml-2 text-red-400 hover:text-red-600"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400 italic">No repayments recorded yet</div>
                    )}

                    {/* Add Repayment */}
                    {!isPaidOff && (
                      repayLoanId === loan.id ? (
                        <div className="bg-white border rounded p-3 space-y-2">
                          <div className="text-xs font-semibold text-green-700 uppercase">Record Repayment</div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                            <div>
                              <Input
                                type="number" step="0.01"
                                value={repayAmount}
                                onChange={(e) => setRepayAmount(e.target.value)}
                                placeholder="Amount"
                                className="h-9 font-mono"
                                data-testid="repay-amount"
                                autoFocus
                              />
                            </div>
                            <div>
                              <Input type="date" value={repayDate} onChange={(e) => setRepayDate(e.target.value)} className="h-9" />
                            </div>
                            <div>
                              <Input value={repayNote} onChange={(e) => setRepayNote(e.target.value)} placeholder="Note (optional)" className="h-9" />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" onClick={() => handleAddRepayment(loan.id)} className="bg-green-600 hover:bg-green-700" data-testid="save-repay-btn">
                              Save Repayment
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setRepayLoanId(null)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => { setRepayLoanId(loan.id); setRepayAmount(""); setRepayDate(today()); setRepayNote(""); }} className="text-green-700" data-testid="add-repay-btn">
                          <Plus className="h-3 w-3 mr-1" /> Add Repayment
                        </Button>
                      )
                    )}

                    {/* Delete Loan */}
                    <div className="pt-2 border-t">
                      <Button size="sm" variant="ghost" onClick={() => handleDeleteLoan(loan)} className="text-red-500 hover:text-red-700 text-xs">
                        <Trash2 className="h-3 w-3 mr-1" /> Delete Loan
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
